import { useEffect, useState } from "react";
import { useAuth, API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { FolderOpen, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { xhrJson } from "../../utils/xhrJson";

export default function AdminRecipeCategories() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/recipe-categories`, { credentials: "include" });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!editing?.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const isNew = !editing.recipe_category_id;
      const url = isNew ? `${API}/admin/recipe-categories` : `${API}/admin/recipe-categories/${editing.recipe_category_id}`;
      const { ok, data } = await xhrJson(isNew ? "POST" : "PUT", url, editing);
      if (!ok) throw new Error(data.detail || "Save failed");
      toast.success(isNew ? "Category created" : "Category updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category? This cannot be undone.")) return;
    try {
      const { ok, data } = await xhrJson("DELETE", `${API}/admin/recipe-categories/${id}`);
      if (!ok) throw new Error(data.detail || "Delete failed");
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <AdminLayout activeNav="recipe-categories">
      <div className="p-4 sm:p-6 space-y-4" data-testid="admin-recipe-categories">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><FolderOpen size={18} /></div>
            <div>
              <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Recipe Categories</h1>
              <p className="text-xs text-gray-500">Group recipes for cooks to browse.</p>
            </div>
          </div>
          <Button onClick={() => setEditing({ name: "", description: "", sort_order: 0 })} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rcat-new-btn">
            <Plus size={14} className="mr-1" /> New category
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No categories yet. Create your first one above.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Description</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Order</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.recipe_category_id} className="border-b border-gray-50 last:border-0" data-testid={`rcat-row-${r.recipe_category_id}`}>
                    <td className="px-3 py-2 font-semibold text-gray-900">{r.name}</td>
                    <td className="px-3 py-2 text-gray-500">{r.description || "—"}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{r.sort_order}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setEditing(r)} data-testid={`rcat-edit-${r.recipe_category_id}`}><Pencil size={10} className="mr-1" />Edit</Button>
                      {user?.role === "admin" && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-600 border-red-200" onClick={() => handleDelete(r.recipe_category_id)} data-testid={`rcat-delete-${r.recipe_category_id}`}><Trash2 size={10} className="mr-1" />Delete</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.recipe_category_id ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} data-testid="rcat-form-name" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Description</Label>
                <Input value={editing.description || ""} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} data-testid="rcat-form-desc" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Sort order</Label>
                <Input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))} data-testid="rcat-form-order" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rcat-form-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
